/**
 * Email the MOU to the client: To / CC / BCC, subject and message prefilled
 * from Settings → MOU, PDF attached, signing link included.
 *
 * Goes out through the sender's own connected mailbox when they have one, so
 * the client sees a real person's address and replies land with them. The
 * "From" line says which it will be before anything is sent.
 */

import { useEffect, useState } from "react";
import { AlertTriangle, Info, Loader2, Mail, Paperclip, Send } from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { MOU_EMAIL_URL } from "../../constants/api";
import MergeFieldEditor from "./MergeFieldEditor";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const badAddrs = (val) => (val || "").split(/[,;]/).map((x) => x.trim()).filter(Boolean)
  .filter((x) => !EMAIL_RE.test(x));

export default function MOUEmailPanel({ mou, onSent, onCancel, onManual }) {
  const { fetch, create } = useApiMethods();

  const [form, setForm]       = useState(null);
  const [meta, setMeta]       = useState({});
  const [showBcc, setShowBcc] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetch(MOU_EMAIL_URL(mou.id)).then((res) => {
      if (res?.status !== "success") {
        ToastMsg("error", res?.message || "Could not prepare the email");
        return;
      }
      const d = res.data;
      setMeta(d);
      setForm({
        to: d.to || "", to_name: d.to_name || "", cc: d.cc || "", bcc: "",
        subject: d.subject || "", body_html: d.body_html || "",
        attach_pdf: d.has_document || d.can_generate,
      });
    });
  }, [mou.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const send = async () => {
    if (!EMAIL_RE.test((form.to || "").trim())) { ToastMsg("error", "Enter a valid recipient email"); return; }
    const bad = [...badAddrs(form.cc), ...badAddrs(form.bcc)];
    if (bad.length) { ToastMsg("error", `Not a valid email: ${bad[0]}`); return; }
    if (!form.subject.trim()) { ToastMsg("error", "Add a subject"); return; }
    setSending(true);
    const res = await create(MOU_EMAIL_URL(mou.id), form);
    setSending(false);
    if (res?.status === "success") {
      ToastMsg("success", res.message || "Sent");
      onSent();
    } else {
      ToastMsg("error", res?.message || "The email could not be sent");
    }
  };

  if (!form) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50/60 p-4 text-xs text-gray-500 dark:border-blue-900/50 dark:bg-blue-900/10">
        <Loader2 className="h-4 w-4 animate-spin" /> Preparing the email…
      </div>
    );
  }

  const willAttach = form.attach_pdf && (meta.has_document || meta.can_generate);

  return (
    <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-900/50 dark:bg-blue-900/10">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white">
          <Mail className="h-4 w-4 text-primary" /> Email the MOU
        </p>
        <p className="text-[11px] text-gray-600 dark:text-gray-400">
          From: {meta.from
            ? <strong>{meta.from}</strong>
            : <>the system mailbox — connect your own under{" "}
                <a className="underline" href="/settings/email-integration" target="_blank" rel="noreferrer">Settings → Email integration</a>{" "}
                so replies come to you</>}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1 sm:col-span-2">
          <label className="text-xs font-medium">To <span className="text-red-500">*</span></label>
          <Input value={form.to} onChange={(e) => set("to", e.target.value)} placeholder="client@hospital.com" />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">Recipient name</label>
          <Input value={form.to_name} onChange={(e) => set("to_name", e.target.value)} />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <label className="text-xs font-medium">CC</label>
          <Input value={form.cc} onChange={(e) => set("cc", e.target.value)}
                 placeholder="Comma separated" />
        </div>
        <div className="flex items-end">
          {showBcc ? (
            <div className="w-full space-y-1">
              <label className="text-xs font-medium">BCC</label>
              <Input value={form.bcc} onChange={(e) => set("bcc", e.target.value)} />
            </div>
          ) : (
            <button className="text-xs text-primary hover:underline" onClick={() => setShowBcc(true)}>
              + Add BCC
            </button>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium">Subject <span className="text-red-500">*</span></label>
        <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} />
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium">Message</label>
        <MergeFieldEditor value={form.body_html} onChange={(v) => set("body_html", v)} height={240} />
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-xs">
        <input type="checkbox" className="h-4 w-4 accent-primary" checked={!!form.attach_pdf}
               disabled={!meta.has_document && !meta.can_generate}
               onChange={(e) => set("attach_pdf", e.target.checked)} />
        <Paperclip className="h-3.5 w-3.5 text-gray-400" />
        Attach the MOU PDF
        {mou.document_name && <span className="text-gray-500">({mou.document_name})</span>}
      </label>

      {willAttach && !meta.has_document && (
        <p className="flex items-start gap-1.5 text-[11px] text-gray-600 dark:text-gray-400">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          No PDF yet — one will be generated from the document text and attached.
        </p>
      )}
      {willAttach && meta.document_stale && (
        <p className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          The document changed since v{mou.version} was generated. A new version will be
          generated and attached, so the client gets what you see.
        </p>
      )}
      {!meta.has_document && !meta.can_generate && (
        <p className="text-[11px] text-gray-600 dark:text-gray-400">
          There is no document to attach. The client can still read and sign the agreement through
          the link in the email.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <button className="text-xs text-gray-500 hover:text-gray-800 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
                onClick={onManual}>
          Sent it another way (WhatsApp, in person)? Mark as sent instead
        </button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" disabled={sending} onClick={send}>
            {sending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1 h-3.5 w-3.5" />}
            Send email
          </Button>
        </div>
      </div>
    </div>
  );
}
